import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js"
import React, { useMemo } from "react"
import { Doughnut } from "react-chartjs-2"
import classNames from "utils/classNames"

ChartJS.register(ArcElement, Tooltip, Legend)
ChartJS.register({
  id: "doughnutProgress",
  afterDatasetsDraw(chart) {
    const pluginConfig = chart.config.options?.plugins?.["doughnutProgress"]
    const {
      chartArea: { left, top, right, bottom },
      ctx,
    } = chart
    const centerX = (left + right) / 2
    const centerY = (top + bottom) / 2
    const r = Math.min(right - left, bottom - top) / 2
    const progress = pluginConfig?.progress > 1 ? 1 : pluginConfig?.progress
    const progressAngle = 2 * Math.PI * progress

    const circleRadius = 4 // Adjust the size of the circles here
    const circleColor = "#0A7380" // Adjust the color of the circles here

    ctx.beginPath()
    ctx.lineWidth = 4
    ctx.strokeStyle = circleColor
    ctx.arc(centerX, centerY, r, -0.5 * Math.PI, progressAngle - 0.5 * Math.PI)
    ctx.stroke()

    if (progress < 0.05) return
    // Draw small filled circles at each end of the stroke
    ctx.beginPath()
    ctx.fillStyle = circleColor
    ctx.arc(
      centerX + Math.cos(-0.5 * Math.PI) * r,
      centerY + Math.sin(-0.5 * Math.PI) * r,
      circleRadius,
      0,
      2 * Math.PI
    )
    ctx.fill()

    ctx.beginPath()
    ctx.fillStyle = circleColor
    ctx.arc(
      centerX + Math.cos(progressAngle - 0.5 * Math.PI) * r,
      centerY + Math.sin(progressAngle - 0.5 * Math.PI) * r,
      circleRadius,
      0,
      2 * Math.PI
    )
    ctx.fill()
    ctx.restore()
  },
})
type DemandDoughnutProps = {
  inwardedQuantity?: number
  timeSlots?: any[]
  demandId: string | number
  isExpanded: boolean
}

const LABEL_COLORS = [
  "#799700",
  "#C8DA81",
  "#c4e500",
  "#d5d900",
  "#e3cc00",
  "#efc000",
  "#f8b300",
  "#ffa600",
]

const DemandDoughnut = ({
  timeSlots,
  inwardedQuantity,
  demandId,
  isExpanded,
}: DemandDoughnutProps) => {
  const { data, backgroundColor, labels, totalQuantity } = useMemo(() => {
    let data: number[] = [],
      backgroundColor: string[] = [],
      labels: string[] = [],
      notBookedQuantity = 0,
      totalQuantity = 0
    if (timeSlots?.length) {
      for (let i = 0; i < timeSlots.length; i++) {
        const timeSlot = timeSlots[i]
        const qtyDifference = timeSlot.quantity - timeSlot.bookedQuantity
        notBookedQuantity += qtyDifference < 0 ? 0 : qtyDifference
        totalQuantity += timeSlot.quantity
        data.push(timeSlot.bookedQuantity)
        labels.push(timeSlot.timeSlot.name)
        backgroundColor.push(LABEL_COLORS[i])
      }
      //   Inwarded Quantity
      labels.push("Inwarded")
      data.push(0)
      backgroundColor.push("#0A7380")

      //   Not Booked
      labels.push("Not Booked")
      data.push(notBookedQuantity)
      backgroundColor.push("white")
    }
    return {
      data,
      backgroundColor,
      labels,
      totalQuantity,
    }
  }, [timeSlots, inwardedQuantity])

  return (
    <div className="flex items-center gap-4 p-4 justify-between h-[225px]">
      <div className="h-full w-[40%]">
        <Doughnut
          id={`demand-doughnut-${demandId}`}
          data={{
            datasets: [
              {
                type: "doughnut",
                data,
                backgroundColor,
                borderColor: "#ced9e0",
                borderWidth: 1,
                hoverBackgroundColor: backgroundColor,
              },
            ],
            labels,
          }}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            layout: {
              padding: 8,
            },
            plugins: {
              ["doughnutProgress" as any]: {
                progress: (inwardedQuantity || 0) / totalQuantity,
                totalQuantity,
                inwardedQuantity,
              },
              legend: {
                display: false,
              },
            },
          }}
        />
      </div>
      <ul className="list-none text-gray-700 w-[60%] overflow-auto">
        {labels.map((label, index) => {
          const bgColor = backgroundColor?.[index]
          const value = data?.[index]
          const isNoData = bgColor === "white"
          const isInwarded = label.trim().toLowerCase() === "inwarded"
          return (
            <li
              className="text-xs mb-2 last:mb-0 flex items-center gap-2"
              key={label}
            >
              <span
                className="inline-block w-[14px] h-[14px] rounded-full border border-gray-300 border-solid"
                style={{
                  backgroundColor: bgColor,
                }}
              />
              <div className="flex items-center gap-1">
                <span>{label}</span>
                {isExpanded && (
                  <div
                    className={classNames(
                      "font-600 flex w-max px-1 py-[2px] rounded",
                      isNoData ? "text-gray-500" : "text-white"
                    )}
                    style={{
                      backgroundColor: isNoData ? "#F5F5F5" : bgColor,
                    }}
                  >
                    {isInwarded ? inwardedQuantity || 0 : value} /{" "}
                    {totalQuantity} Units
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default DemandDoughnut
